import type { InferOutput } from 'valibot'
import { array, number, object, optional, string, union } from 'valibot'

export const suggestionDetailSchema = object({
  numFound: number(),
  startOffset: number(),
  endOffset: number(),
  origFreq: optional(number()),
  suggestion: optional(array(union([
    string(),
    object({ word: string(), freq: number() }),
  ]))),
})

/**
 *  Transform Solr's alternating suggestions array format
 *  From: ["misspelled", {numFound, startOffset, ...}, "another", {...}]
 *  To: [{term: "misspelled", detail: {numFound, startOffset, ...}}, {term: "another", detail: {...}}]
 */
export function transformSuggestions(array: (string | InferOutput<typeof suggestionDetailSchema>)[]): Array<{ term: string, detail: InferOutput<typeof suggestionDetailSchema> }> {
  const result: Array<{ term: string, detail: InferOutput<typeof suggestionDetailSchema> }> = []

  for (let i = 0; i < array.length; i += 2) {
    const term = array[i]
    const detail = array[i + 1]
    if (typeof term === 'string' && typeof detail === 'object') {
      result.push({ term, detail })
    }
  }

  return result
}

export const collationDetailSchema = object({
  collationQuery: string(),
  hits: number(),
  /** Alternating pairs: ["misspelled", "correction", ...] */
  misspellingsAndCorrections: array(string()),
})

/**
 * Transform Solr's alternating collation array format
 * Extended (collateExtendedResults=true): ["collation", {collationQuery, hits, ...}, "collation", {...}]
 * Simple (collateExtendedResults=false): ["collation", "barnerett", "collation", "bankrett"]
 * Output: [{collationQuery, hits, ...}]
 */
export function transformCollations(arr: (string | InferOutput<typeof collationDetailSchema>)[]): Array<InferOutput<typeof collationDetailSchema>> {
  const result: Array<InferOutput<typeof collationDetailSchema>> = []
  for (let i = 0; i < arr.length; i += 2) {
    const item = arr[i + 1]
    if (item && typeof item === 'object' && 'collationQuery' in item) {
      result.push(item)
    }
    else if (typeof item === 'string') {
      result.push({ collationQuery: item, hits: 0, misspellingsAndCorrections: [] })
    }
  }
  return result
}

type SpellcheckCandidate = string | { word: string, freq: number }

/**
 * Pick the highest-frequency candidate. When origFreq is known, accept only
 * candidates that are more frequent than the user's original term.
 */
export function pickHighestFreq(candidates: SpellcheckCandidate[] | undefined, origFreq?: number): string | null {
  if (!candidates || candidates.length === 0) {
    return null
  }

  let best: { word: string, freq: number } | null = null
  for (const candidate of candidates) {
    if (typeof candidate === 'string' || (origFreq !== undefined && candidate.freq <= origFreq)) {
      continue
    }
    if (!best || candidate.freq > best.freq) {
      best = candidate
    }
  }

  if (!best && origFreq === undefined) {
    const first = candidates[0]
    return typeof first === 'string' ? first : first?.word ?? null
  }

  return best?.word ?? null
}
